import { useMemo, useRef, useState, useLayoutEffect } from 'react';
import ReactECharts from 'echarts-for-react/lib/core';
import echarts from '../../lib/echarts.js';
import { cellText, formatNumber, formatAxis } from '../../lib/format.js';
import { useDrill } from '../../lib/DrillContext.jsx';
import { useTheme } from '../../lib/theme.jsx';
import { chartPalette, brandPrimary } from '../../lib/brand.js';
import { orderRows, orderPivots, categoryAxisInverse } from '../../lib/lookerOrder.js';
import { groupExtraDimension } from '../../lib/chartPivot.js';
import { visibleColumns, visibleForPivot, isHiddenField } from '../../lib/visibleFields.mjs';

// Howler-branded chart renderer (Apache ECharts): gradient fills, rounded bars,
// staggered load animation, branded tooltips. Always uses the Howler palette
// (ignores Looker's colours by design). Supports column/bar/line/area/pie/
// doughnut/scatter, pivoted series, table calculations, and drill-down.

// Palette comes from the brand engine: Howler's hand-tuned palette by default,
// or one generated from a white-labelled client's primary+secondary pair.
const color = (i) => { const P = chartPalette(); return P[i % P.length]; };

function hexToRgba(hex, a) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
const num = (v) => (v == null || v === '' ? null : Number(v));

// Vertical (or horizontal) gradient for bars.
function barGradient(c, horizontal) {
  const stops = [{ offset: 0, color: c }, { offset: 1, color: hexToRgba(c, 0.6) }];
  return horizontal
    ? new echarts.graphic.LinearGradient(0, 0, 1, 0, stops)
    : new echarts.graphic.LinearGradient(0, 0, 0, 1, stops);
}
// Soft area gradient for line/area fills.
function areaGradient(c) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: hexToRgba(c, 0.45) },
    { offset: 1, color: hexToRgba(c, 0.02) },
  ]);
}

// `query` (the tile's saved Looker query) is optional: with it, categories follow
// the tile's sort and sales phases read in order (see lib/lookerOrder.js).
export default function ChartTile({ data, visConfig = {}, zoom = 0, query = null }) {
  const { openDrill } = useDrill();
  // Measure the available height so we can truncate rotated axis names to fit
  // the plot area (short tiles can't show a long vertical title).
  const boxRef = useRef(null);
  const [boxH, setBoxH] = useState(280);
  useLayoutEffect(() => {
    if (!boxRef.current) return;
    const el = boxRef.current;
    const ro = new ResizeObserver(() => setBoxH(el.clientHeight || 280));
    ro.observe(el);
    setBoxH(el.clientHeight || 280);
    return () => ro.disconnect();
  }, []);
  const fields = data.fields || {};
  // Ordering runs first (Looker's order — lib/lookerOrder.js), so
  // both the zoom below and any series grouping inherit it. `pulse_order` is the
  // server saying which sort actually ran — it isn't always the tile's own
  // (server/rowOrder.js).
  const allRows = orderRows(data.data || [], fields, query, data.pulse_order?.sorts);
  const pieLike = (visConfig.type === 'looker_pie' || visConfig.type === 'looker_donut_multiples');
  // Honour Looker's hidden fields (e.g. a raw measure hidden in favour of a
  // running-total / % change calc) — otherwise extra squashed series appear, and
  // a hidden breakdown dimension groups the chart by something Looker doesn't
  // plot (../../lib/visibleFields.mjs).
  const { dimensions: visDims, measures } = visibleColumns(fields, visConfig);
  // …but a HIDDEN dimension still splits the rows, so chartPivot.js is told
  // about it too: it may lead the legend when no visible dimension can, which
  // beats drawing the same category twice. It is never used as the axis.
  const hiddenDims = (fields.dimensions || []).filter((d) => d?.name && isHiddenField(visConfig, d.name));
  // A second row dimension with no pivot (a YoY tile that was never pivoted in
  // Looker) repeats each category on the x-axis. Fold it into the pivoted shape
  // so the years group side by side instead — see lib/chartPivot.js. Pie-likes
  // read one dimension by definition and are left alone. Rows already carry
  // Looker/sales-phase order at this point, so the series this produces inherit
  // it (e.g. "Sales Per Phase/Local vs International" — the tile that motivated
  // both features).
  const grouped = useMemo(
    () => (pieLike ? null : groupExtraDimension({ rows: allRows, dimensions: visDims, measures, pivots: data.pivots || [], hiddenDimensions: hiddenDims })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, pieLike, visConfig.hidden_fields, query]
  );
  const baseRows = grouped ? grouped.rows : allRows;
  const dimensions = grouped ? grouped.dimensions : visDims;
  // Series order = Looker's own column_order (a hand-arranged pivot order the
  // table already honoured), else Looker's own pivot order — applies to a
  // grouped tile's synthetic series too, since its pivot label is just the
  // series value.
  const pivots = orderPivots(grouped ? grouped.pivots : (data.pivots || []), visConfig, measures.map((m) => m.name));
  // Per-user tile zoom: show only the LAST N points (the interesting end of a
  // long date / days-before axis). Pie-likes keep every category.
  const rows = (zoom > 0 && !pieLike && baseRows.length > zoom) ? baseRows.slice(-zoom) : baseRows;
  const visType = visConfig?.type || 'looker_column';

  // seriesMeta[seriesIndex] = { measure, pivotKey, fmt } for tooltip + drill.
  const stacked = visConfig.stacking === 'normal' || visConfig.stacking === 'percent';
  const { theme } = useTheme();
  // Rebuild colours when the white-label brand pair changes (palette is read
  // inside buildOption, so the brand version must be a dependency).
  const [brandV, setBrandV] = useState(0);
  useLayoutEffect(() => {
    const f = () => setBrandV((v) => v + 1);
    window.addEventListener('brand-changed', f);
    return () => window.removeEventListener('brand-changed', f);
  }, []);
  // rows/dimensions/measures/pivots all derive from `data`, and a rendered
  // tile's `visConfig` is stable (visType/stacked already cover what changes at
  // runtime). Keying on the `visConfig` object would rebuild the chart every
  // render, since it defaults to a fresh {} — so depend on the source instead.
  const { option, seriesMeta } = useMemo(
    () => buildOption({ rows, dimensions, measures, pivots, visType, stacked, visConfig, boxH }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, zoom, visType, stacked, boxH, theme, brandV, query]
  );

  if (!rows.length || !measures.length) return <Empty />;

  const onClick = (params) => {
    const row = rows[params.dataIndex];
    if (!row) return;
    const meta = visType.includes('pie') || visType.includes('donut')
      ? { measure: measures[0]?.name }
      : seriesMeta[params.seriesIndex];
    if (!meta?.measure) return;
    const cell = meta.pivotKey ? row[meta.measure]?.[meta.pivotKey] : row[meta.measure];
    const links = cell?.links;
    if (links?.length) {
      const dim = dimensions[0] ? cellText(row[dimensions[0].name]) : '';
      openDrill(links, [dim, params.seriesName].filter(Boolean).join(' · '));
    }
  };

  return (
    <div ref={boxRef} style={{ width: '100%', height: '100%', padding: 6 }}>
      <ReactECharts
        echarts={echarts}
        option={option}
        notMerge
        style={{ width: '100%', height: '100%' }}
        opts={{ renderer: 'canvas' }}
        onEvents={{ click: onClick }}
      />
    </div>
  );
}

function buildOption({ rows, dimensions, measures, pivots, visType, stacked, visConfig = {}, boxH = 280 }) {
  const dark = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark';
  const axisC = dark ? '#9a9aa3' : '#888';
  const nameC = dark ? '#8a8a92' : '#9a9a9a';
  const splitC = dark ? 'rgba(255,255,255,0.08)' : '#f2f2f2';
  const axisLineC = dark ? 'rgba(255,255,255,0.14)' : '#e6e6e6';
  const labelC = dark ? '#c0c0c9' : '#5a5a5a';
  const tip = dark
    ? { ...tooltipStyle, backgroundColor: 'rgba(28,28,34,0.97)', borderColor: 'rgba(255,255,255,0.12)', textStyle: { color: '#f3f3f6', fontSize: 12 } }
    : tooltipStyle;
  const isPie = visType === 'looker_pie' || visType === 'looker_donut_multiples';
  const isDonut = visType === 'looker_donut_multiples';
  const isBar = visType === 'looker_bar';       // horizontal
  const isArea = visType === 'looker_area';
  const isLine = visType === 'looker_line' || isArea;
  const isScatter = visType === 'looker_scatter';

  const primaryDim = dimensions[0];
  const labels = rows.map((r) => (primaryDim ? cellText(r[primaryDim.name]) : ''));
  const seriesMeta = [];

  // First paint: grow in with a per-point stagger. Updates (filter changes):
  // morph the existing shapes to their new values instead of redrawing.
  const baseAnim = {
    animationDuration: 800,
    animationEasing: 'cubicOut',
    animationDelay: (i) => i * 18,
    animationDurationUpdate: 420,
    animationEasingUpdate: 'cubicInOut',
    animationDelayUpdate: (i) => i * 6,
  };

  // ─── Pie / doughnut ──────────────────────────────────────────────────────────
  if (isPie) {
    const m = measures[0];
    const pieData = rows.map((r, i) => ({ name: labels[i], value: num(r[m?.name]?.value) }));
    seriesMeta[0] = { measure: m?.name, fmt: m?.value_format };
    return {
      seriesMeta,
      option: {
        ...baseAnim,
        color: chartPalette(),
        tooltip: {
          trigger: 'item',
          ...tip,
          formatter: (p) => `${p.marker} ${p.name}<b style="margin-left:10px">${formatNumber(p.value, m?.value_format)}</b> <span style="color:#999">(${p.percent}%)</span>`,
        },
        legend: { bottom: 0, type: 'scroll', textStyle: { fontSize: 11 }, icon: 'circle' },
        series: [{
          type: 'pie',
          radius: isDonut ? ['42%', '70%'] : '72%',
          center: ['50%', '46%'],
          data: pieData,
          itemStyle: { borderColor: dark ? '#1a1a1f' : '#fff', borderWidth: 2, borderRadius: 6 },
          label: { show: false },
          emphasis: { scale: true, scaleSize: 6, itemStyle: { shadowBlur: 12, shadowColor: 'rgba(0,0,0,0.2)' } },
        }],
      },
    };
  }

  // ─── Bars / lines / area / scatter ───────────────────────────────────────────
  // Dual y-axis: Looker's y_axes maps each measure (axisId) to a left/right
  // axis. Only use two axes when visible measures actually span both sides.
  const yMap = {};
  for (const ax of (visConfig.y_axes || [])) for (const s of (ax.series || [])) if (s.axisId) yMap[s.axisId] = ax.orientation;
  const orientationOf = (m) => yMap[m.name] || 'left';
  const hasRight = !isBar && measures.some((m) => orientationOf(m) === 'right');
  const hasLeft = measures.some((m) => orientationOf(m) !== 'right');
  // Auto dual-axis: with exactly two measures and NO explicit Looker y_axes config
  // (e.g. the Owl's two-measure charts), give the 2nd measure its own right axis so a
  // small-scale series (tickets) isn't flattened next to a large one (revenue). Skip
  // when stacked (one axis is intended), horizontal bars, pie or scatter.
  // …but only when there are genuinely two scales to separate. A second axis
  // exists to rescue a small series from being flattened by a large one; if one
  // of the two measures has no non-zero value there is nothing to rescue, and
  // the axis is actively misleading — an all-zero "Organic" series drew its own
  // 0-1 axis next to a 0-300 one, which reads as two different units.
  const hasValues = (m) => rows.some((r) => {
    const cell = r[m.name];
    if (!cell) return false;
    if (cell.value != null) return Number(cell.value) !== 0;
    return Object.values(cell).some((c) => c && Number(c.value) !== 0); // pivoted
  });
  const autoDual = !(visConfig.y_axes || []).length && measures.length === 2 && !stacked && !isBar && !isScatter
    && measures.every(hasValues);
  const dual = (hasRight && hasLeft) || autoDual;
  const yIndexOf = (m) => {
    if (hasRight && hasLeft) return orientationOf(m) === 'right' ? 1 : 0;
    if (autoDual) return measures.indexOf(m) === 1 ? 1 : 0;
    return 0;
  };
  // Looker "Values" data labels printed on each point/bar.
  const showLabels = visConfig.show_value_labels === true;

  // Combination charts: Looker's series_types overrides individual series to a
  // different type (e.g. a column chart where some years render as lines). Keys
  // are "<pivot value> - <measure name>" (pivoted) or "<measure name>".
  const baseFlags = { isBar, isLine, isArea, isScatter };
  const st = visConfig.series_types || {};
  const flagsFor = (type) => {
    switch (type) {
      case 'line': return { isBar: false, isLine: true, isArea: false, isScatter: false };
      case 'area': return { isBar: false, isLine: true, isArea: true, isScatter: false };
      case 'column':
      case 'bar': return { isBar: false, isLine: false, isArea: false, isScatter: false };
      case 'scatter': return { isBar: false, isLine: false, isArea: false, isScatter: true };
      default: return baseFlags;
    }
  };
  const typeFor = (pivotKey, mName) => {
    const key = pivotKey == null ? mName : `${pivotKey} - ${mName}`;
    if (st[key]) return st[key];
    if (pivotKey != null) {
      for (const [k, v] of Object.entries(st)) {
        const parts = k.split(' - ');
        if (parts.length >= 2 && parts[parts.length - 1] === mName && parts.slice(0, -1).join(' - ') === String(pivotKey)) return v;
      }
      // A bare measure-name override styles every pivot of that measure.
      if (st[mName]) return st[mName];
      // The config predates this pivot value (a NEW event/year appeared since the
      // tile was set up, so only e.g. "<last year> - cumulative" is keyed): inherit
      // the type this measure has under the OTHER pivot values when they all agree
      // — a cumulative line stays a line for the new year too.
      const others = Object.entries(st).filter(([k]) => k.endsWith(` - ${mName}`)).map(([, v]) => v);
      if (others.length && others.every((v) => v === others[0])) return others[0];
    }
    return null;
  };

  let series = [];
  if (pivots.length > 0) {
    const multi = measures.length > 1;
    pivots.forEach((pivot, pi) => {
      const plabel = pivot.data ? Object.values(pivot.data).join(' / ') : pivot.key;
      // Looker can hide a measure under ONE pivot column only (hidden_pivots).
      const shownHere = new Set(visibleForPivot(measures, visConfig, pivot.key).map((m) => m.name));
      measures.forEach((m, mi) => {
        if (!shownHere.has(m.name)) return; // hidden per-pivot
        // A measure whose cells are NOT keyed by pivot belongs to the chart as a
        // whole, not to one pivot column — a Pulse measure merged onto a pivoted
        // Looker tile (server/leadFeeds.js) is the case in hand. Drawn once, on
        // the first pass, instead of once per pivot as an empty series each time.
        if (rows.some((r) => r[m.name] && r[m.name].value !== undefined)) {
          if (pi > 0) return;
          seriesMeta[series.length] = { measure: m.name, fmt: m.value_format };
          series.push(makeSeries(m.label_short || m.label, rows.map((r) => num(r[m.name]?.value)), measures.length * pivots.length + mi,
            { ...flagsFor(typeFor(null, m.name)), stacked, yAxisIndex: yIndexOf(m), showLabels, labelC, fmt: m.value_format }));
          return;
        }
        const idx = pi * measures.length + mi;
        const name = multi ? `${plabel} — ${m.label_short || m.label}` : plabel;
        seriesMeta[series.length] = { measure: m.name, pivotKey: pivot.key, fmt: m.value_format };
        series.push(makeSeries(name, rows.map((r) => num(r[m.name]?.[pivot.key]?.value)), idx, { ...flagsFor(typeFor(pivot.key, m.name)), stacked, yAxisIndex: yIndexOf(m), showLabels, labelC, fmt: m.value_format }));
      });
    });
  } else {
    measures.forEach((m, i) => {
      seriesMeta[series.length] = { measure: m.name, fmt: m.value_format };
      series.push(makeSeries(m.label_short || m.label, rows.map((r) => num(r[m.name]?.value)), i, { ...flagsFor(typeFor(null, m.name)), stacked, yAxisIndex: yIndexOf(m), showLabels, labelC, fmt: m.value_format }));
    });
  }

  const fmtFor = (axisIdx) => (measures.find((m) => yIndexOf(m) === axisIdx) || measures[0])?.value_format;
  // Axis titles — match Looker: y_axes[].label (or the measure label), and
  // x_axis_label (or the dimension label).
  // Truncate rotated y-axis names to the available plot height (short tiles
  // can't fit a long vertical title) — ~5.6px per char at fontSize 10.
  const clip = (s, max) => (s && s.length > max ? s.slice(0, Math.max(1, max - 1)).trimEnd() + '…' : (s || ''));
  const yBudget = Math.max(8, Math.floor((boxH - 80) / 5.6));
  const yNameRaw = (axisIdx) => {
    const cfg = (visConfig.y_axes || []).find((ax) => (ax.orientation === 'right') === (axisIdx === 1));
    if (cfg && cfg.label) return cfg.label;
    const m = measures.find((mm) => yIndexOf(mm) === axisIdx);
    return m ? (m.label_short || m.label || '') : '';
  };
  const yName = (axisIdx) => clip(yNameRaw(axisIdx), yBudget);
  const xNameRaw = visConfig.show_x_axis_label === false ? '' : (visConfig.x_axis_label || primaryDim?.label || primaryDim?.label_short || '');
  const xName = isBar ? clip(xNameRaw, yBudget) : clip(xNameRaw, 70);

  const nameStyle = { fontSize: 10, color: nameC, fontWeight: 500 };
  const valueAxis = (axisIdx, position) => ({
    type: 'value', position,
    name: yName(axisIdx), nameLocation: 'middle', nameGap: 46, nameRotate: position === 'right' ? -90 : 90,
    nameTextStyle: nameStyle,
    axisLabel: { fontSize: 10, color: axisC, formatter: (v) => formatAxis(v, fmtFor(axisIdx)) },
    splitLine: { lineStyle: { color: splitC } },
    axisLine: { show: false }, axisTick: { show: false },
  });
  // Category labels (e.g. full event names) can be long and often share a prefix
  // ("Kappa FuturFestival 2025…" / "…2026"). A blind end-truncation makes them look
  // identical — fatal in a downloaded image where there's no tooltip. So: strip the
  // shared prefix (the distinguishing part is usually the tail), then truncate to a
  // length that scales with how many labels there are (fewer bars → more room). The
  // tooltip still shows the full raw value.
  const commonPrefix = (() => {
    const xs = labels.filter((s) => s != null && s !== '');
    if (xs.length < 2) return '';
    let p = String(xs[0]);
    for (const s of xs) { while (p && !String(s).startsWith(p)) p = p.slice(0, -1); if (!p) break; }
    return p.length >= 8 ? p : ''; // only strip a substantial shared prefix
  })();
  const catMax = labels.length <= 3 ? 30 : labels.length <= 6 ? 22 : 16;
  const catLabel = (v) => {
    let s = String(v ?? '');
    if (commonPrefix && s.startsWith(commonPrefix) && s.length > commonPrefix.length) s = `…${s.slice(commonPrefix.length).replace(/^[\s\-–—:]+/, '')}`;
    return s.length > catMax ? `${s.slice(0, catMax - 1).trimEnd()}…` : s;
  };
  const catAxis = {
    type: 'category', data: labels, boundaryGap: true,
    // Looker draws a horizontal bar chart's first row at the TOP; ECharts would
    // put it at the bottom and read every tier list upside down. Looker's
    // "Reverse axis" (x_axis_reversed) flips either way (lib/lookerOrder.js).
    inverse: categoryAxisInverse({ ...visConfig, type: visType }),
    name: isBar ? '' : xName, nameLocation: 'middle', nameGap: 28, nameTextStyle: nameStyle,
    axisLabel: { fontSize: 10, color: axisC, hideOverlap: true, rotate: labels.length > 8 && !isBar ? 35 : 0, formatter: catLabel },
    axisLine: { lineStyle: { color: axisLineC } }, axisTick: { show: false },
  };

  const xAxis = isBar ? valueAxis(0, 'bottom') : catAxis;
  const yAxis = isBar
    ? catAxis
    : (dual ? [valueAxis(0, 'left'), valueAxis(1, 'right')] : [valueAxis(0, 'left')]);

  const showLegend = series.length > 1;
  // Reserve room for rotated axis names so they aren't clipped at the edges
  // (containLabel fits tick labels but not the offset axis name).
  const leftName = isBar ? 0 : (yName(0) ? 20 : 0);
  const rightName = (!isBar && dual && yName(1)) ? 20 : 0;
  const xNameSpace = (isBar ? (yName(0) ? 18 : 0) : (xName ? 18 : 0));
  return {
    seriesMeta,
    option: {
      ...baseAnim,
      color: chartPalette(),
      grid: { left: 6 + leftName, right: (dual ? 6 : 14) + rightName, top: 12, bottom: (showLegend ? 34 : 18) + xNameSpace, containLabel: true },
      tooltip: {
        trigger: isScatter ? 'item' : 'axis',
        ...tip,
        axisPointer: { type: isBar ? 'line' : 'shadow', shadowStyle: { color: hexToRgba(brandPrimary(), 0.06) } },
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params];
          const title = arr[0]?.name || arr[0]?.axisValueLabel || ''; // raw category (full), not the truncated axis label
          let s = `<div style="font-weight:700;margin-bottom:4px">${title}</div>`;
          for (const p of arr) {
            const fmt = seriesMeta[p.seriesIndex]?.fmt;
            const raw = Array.isArray(p.value) ? p.value[isBar ? 0 : 1] : p.value;
            s += `<div style="display:flex;gap:14px;align-items:center"><span>${p.marker} ${p.seriesName}</span><b style="margin-left:auto">${formatNumber(raw, fmt)}</b></div>`;
          }
          return s;
        },
      },
      legend: showLegend ? { bottom: 0, type: 'scroll', textStyle: { fontSize: 11 }, icon: 'roundRect' } : undefined,
      xAxis,
      yAxis,
      series,
    },
  };
}

function makeSeries(name, vals, idx, { isBar, isLine, isArea, isScatter, stacked, yAxisIndex = 0, showLabels = false, labelC = '#5a5a5a', fmt }) {
  const c = color(idx);
  // Value labels on each point/bar (Looker's show_value_labels). For dense
  // line/area/scatter series, thin the labels so a readable subset shows
  // (≈ Looker's label_density). Thin by non-null points — series are often
  // sparse (e.g. 7 values across a 30-day axis).
  const isPoint = isLine || isScatter;
  const nonNull = [];
  vals.forEach((v, i) => { if (v != null) nonNull.push(i); });
  const lstep = isPoint ? Math.max(1, Math.ceil(nonNull.length / 8)) : 1;
  const labelSet = new Set(nonNull.filter((_, k) => k % lstep === 0 || k === nonNull.length - 1));
  const label = showLabels ? {
    show: true, position: isBar ? 'right' : 'top', distance: 4,
    fontSize: 9, color: labelC, fontWeight: 500,
    formatter: (p) => {
      if (!labelSet.has(p.dataIndex)) return '';
      const v = Array.isArray(p.value) ? p.value[p.value.length - 1] : p.value;
      if (v == null) return '';
      // Abbreviate only wide labels (e.g. 24,114,666.98 → 24.1M) so big
      // numbers don't overlap, while short values stay exact.
      const full = formatNumber(v, fmt);
      return full.replace('-', '').length > 7 ? formatAxis(v, fmt) : full;
    },
  } : undefined;
  const labelLayout = showLabels && isBar ? { hideOverlap: true } : undefined;
  if (isScatter) {
    return { name, type: 'scatter', yAxisIndex, data: vals, symbolSize: 10, itemStyle: { color: hexToRgba(c, 0.8) }, label, labelLayout };
  }
  if (isLine) {
    return {
      // ECharts line labels only render at shown symbols, so reveal small
      // symbols when value labels are enabled.
      name, type: 'line', yAxisIndex, data: vals, smooth: true,
      showSymbol: showLabels, symbolSize: showLabels ? 4 : 6,
      lineStyle: { width: 3, color: c }, itemStyle: { color: c },
      areaStyle: isArea ? { color: areaGradient(c) } : undefined,
      stack: stacked ? 'total' : undefined,
      label, labelLayout,
      emphasis: { focus: 'series' },
    };
  }
  // bar (vertical column or horizontal bar)
  return {
    name, type: 'bar', yAxisIndex, data: vals,
    barMaxWidth: 38,
    itemStyle: {
      color: barGradient(c, isBar),
      borderRadius: isBar ? [0, 6, 6, 0] : [6, 6, 0, 0],
    },
    stack: stacked ? 'total' : undefined,
    label, labelLayout,
    emphasis: { focus: 'series', itemStyle: { shadowBlur: 10, shadowColor: hexToRgba(c, 0.4) } },
  };
}

const tooltipStyle = {
  backgroundColor: '#fff',
  borderColor: '#eee',
  borderWidth: 1,
  padding: [8, 12],
  textStyle: { color: '#222', fontSize: 12 },
  extraCssText: 'border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.12);',
};

function Empty() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ccc', fontSize: 12 }}>No data</div>;
}
